/**
 * useDrawingTools - Rhino-style drawing tools with preview and dimension input
 *
 * Provides unified workflow for all geometry tools:
 * 1. Click to set center/start point
 * 2. Move mouse for live preview
 * 3. Click second point OR type dimension to confirm
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Drawing tool phases
 */
export const DRAWING_PHASES = {
  IDLE: 'idle',           // No tool active
  FIRST_POINT: 'first',   // Waiting for first point
  SECOND_POINT: 'second', // Waiting for second point (with preview)
  DIMENSION: 'dimension', // Entering dimension value
};

/**
 * Tool configurations - defines input workflow for each tool
 */
const TOOL_CONFIG = {
  circle: {
    firstPointLabel: 'Mittelpunkt',
    secondPointLabel: 'Radius',
    dimensionLabel: 'Radius (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2) => ({
      type: 'circle',
      center: p1,
      radius: Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    }),
  },
  rectangle: {
    firstPointLabel: 'Erste Ecke',
    secondPointLabel: 'Gegenüber',
    dimensionLabel: 'Breite,Höhe (m)',
    dimensionUnit: 'm',
    twoValues: true, // Allows "width,height" input
    getDimension: (p1, p2) => ({
      width: Math.abs(p2.x - p1.x),
      height: Math.abs(p2.y - p1.y),
    }),
    applyDimension: (p1, dim, _angle, raw) => {
      // Parse "width,height" or just one value
      const parts = raw.split(/[,;x]/);
      const width = parseFloat(parts[0]) * 1000;
      const height = parts[1] ? parseFloat(parts[1]) * 1000 : width;
      return { x: p1.x + width, y: p1.y + height };
    },
    getPreview: (p1, p2) => ({
      type: 'rectangle',
      corner1: p1,
      corner2: p2,
    }),
  },
  polygon: {
    firstPointLabel: 'Mittelpunkt',
    secondPointLabel: 'Eckpunkt',
    dimensionLabel: 'Radius (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2) => {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return {
        type: 'polygon',
        center: p1,
        radius: Math.sqrt(dx * dx + dy * dy),
        rotation: Math.atan2(dy, dx),
        sides: 6, // Default hexagon
      };
    },
  },
  cylinder: {
    firstPointLabel: 'Basismittelpunkt',
    secondPointLabel: 'Radius',
    dimensionLabel: 'Radius (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2, storeyHeight) => ({
      type: 'cylinder',
      center: p1,
      radius: Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
      height: storeyHeight || 2750,
    }),
  },
  sphere: {
    firstPointLabel: 'Mittelpunkt',
    secondPointLabel: 'Radius',
    dimensionLabel: 'Radius (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2) => {
      const radius = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
      return {
        type: 'sphere',
        center: { ...p1, z: radius },
        radius,
      };
    },
  },
  box: {
    firstPointLabel: 'Erste Ecke',
    secondPointLabel: 'Gegenüber',
    dimensionLabel: 'Breite,Tiefe (m)',
    dimensionUnit: 'm',
    twoValues: true,
    getDimension: (p1, p2) => ({
      width: Math.abs(p2.x - p1.x),
      depth: Math.abs(p2.y - p1.y),
    }),
    applyDimension: (p1, _dim, _angle, raw) => {
      const parts = raw.split(/[,;x]/);
      const width = parseFloat(parts[0]) * 1000;
      const depth = parts[1] ? parseFloat(parts[1]) * 1000 : width;
      return { x: p1.x + width, y: p1.y + depth };
    },
    getPreview: (p1, p2, storeyHeight) => ({
      type: 'box',
      corner1: p1,
      corner2: p2,
      height: storeyHeight || 2750,
    }),
  },
  line: {
    firstPointLabel: 'Startpunkt',
    secondPointLabel: 'Endpunkt',
    dimensionLabel: 'Länge (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2) => ({
      type: 'line',
      start: p1,
      end: p2,
    }),
  },
  construction_line: {
    firstPointLabel: 'Punkt 1',
    secondPointLabel: 'Punkt 2',
    dimensionLabel: 'Länge (m)',
    dimensionUnit: 'm',
    getDimension: (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2),
    applyDimension: (p1, dim, angle) => ({
      x: p1.x + dim * Math.cos(angle),
      y: p1.y + dim * Math.sin(angle),
    }),
    getPreview: (p1, p2) => ({
      type: 'construction_line',
      point1: p1,
      point2: p2,
    }),
  },
};

/**
 * Hook for managing Rhino-style drawing tools with preview
 */
export function useDrawingTools({ activeTool, storeyHeight = 2750 }) {
  const [phase, setPhase] = useState(DRAWING_PHASES.IDLE);
  const [firstPoint, setFirstPoint] = useState(null);
  const [currentMousePos, setCurrentMousePos] = useState(null);
  const [dimensionInput, setDimensionInput] = useState('');
  const [showDimensionInput, setShowDimensionInput] = useState(false);

  // Get config for current tool
  const config = useMemo(() => {
    const toolKey = activeTool?.toLowerCase?.() || '';
    return TOOL_CONFIG[toolKey] || null;
  }, [activeTool]);

  // Check if current tool supports drawing preview
  const isDrawingTool = useMemo(() => {
    return config !== null;
  }, [config]);

  // Calculate preview based on first point and mouse position
  const preview = useMemo(() => {
    if (!config || !firstPoint || !currentMousePos) return null;
    if (phase !== DRAWING_PHASES.SECOND_POINT) return null;

    return config.getPreview(firstPoint, currentMousePos, storeyHeight);
  }, [config, firstPoint, currentMousePos, phase, storeyHeight]);

  // Calculate dimension value for display
  const currentDimension = useMemo(() => {
    if (!config || !firstPoint || !currentMousePos) return null;
    if (phase !== DRAWING_PHASES.SECOND_POINT) return null;

    return config.getDimension(firstPoint, currentMousePos);
  }, [config, firstPoint, currentMousePos, phase]);

  // Calculate angle from first point to mouse
  const currentAngle = useMemo(() => {
    if (!firstPoint || !currentMousePos) return 0;
    return Math.atan2(
      currentMousePos.y - firstPoint.y,
      currentMousePos.x - firstPoint.x
    );
  }, [firstPoint, currentMousePos]);

  // Start drawing (first click)
  const startDrawing = useCallback((x, y) => {
    if (!config) return false;

    setFirstPoint({ x, y, z: 0 });
    setPhase(DRAWING_PHASES.SECOND_POINT);
    setDimensionInput('');
    return true;
  }, [config]);

  // Update mouse position for preview
  const updateMousePosition = useCallback((x, y) => {
    if (phase === DRAWING_PHASES.SECOND_POINT) {
      setCurrentMousePos({ x, y, z: 0 });
    }
  }, [phase]);

  // Get second point from dimension input
  const getPointFromDimension = useCallback((input) => {
    if (!config || !firstPoint) return null;

    const raw = input.trim().replace(',', '.');

    if (config.twoValues) {
      // For rectangle/box: parse width,height
      return config.applyDimension(firstPoint, null, currentAngle, input);
    } else {
      // For circle/polygon: parse single radius
      const value = parseFloat(raw) * 1000; // m → mm
      if (isNaN(value) || value <= 0) return null;
      return config.applyDimension(firstPoint, value, currentAngle);
    }
  }, [config, firstPoint, currentAngle]);

  // Confirm with current mouse position
  const confirmWithMouse = useCallback(() => {
    if (!firstPoint || !currentMousePos) return null;

    const result = {
      firstPoint,
      secondPoint: currentMousePos,
      preview,
    };

    reset();
    return result;
  }, [firstPoint, currentMousePos, preview]);

  // Confirm with dimension input
  const confirmWithDimension = useCallback((input) => {
    const secondPoint = getPointFromDimension(input);
    if (!secondPoint || !firstPoint) return null;

    const result = {
      firstPoint,
      secondPoint,
      preview: config?.getPreview(firstPoint, secondPoint, storeyHeight),
    };

    reset();
    return result;
  }, [firstPoint, getPointFromDimension, config, storeyHeight]);

  // Reset state
  const reset = useCallback(() => {
    setPhase(DRAWING_PHASES.IDLE);
    setFirstPoint(null);
    setCurrentMousePos(null);
    setDimensionInput('');
    setShowDimensionInput(false);
  }, []);

  // Show dimension input dialog
  const showInput = useCallback(() => {
    if (phase === DRAWING_PHASES.SECOND_POINT) {
      setShowDimensionInput(true);
    }
  }, [phase]);

  // Hide dimension input dialog
  const hideInput = useCallback(() => {
    setShowDimensionInput(false);
    setDimensionInput('');
  }, []);

  // Status text for UI
  const statusText = useMemo(() => {
    if (!config) return '';

    switch (phase) {
      case DRAWING_PHASES.FIRST_POINT:
      case DRAWING_PHASES.IDLE:
        return `${config.firstPointLabel} anklicken`;
      case DRAWING_PHASES.SECOND_POINT:
        return `${config.secondPointLabel} anklicken oder Zahl eingeben`;
      default:
        return '';
    }
  }, [config, phase]);

  // Format dimension for display (mm → m)
  const formattedDimension = useMemo(() => {
    if (!currentDimension) return null;

    if (typeof currentDimension === 'object') {
      // Rectangle/Box dimensions
      return `${(currentDimension.width / 1000).toFixed(2)} × ${((currentDimension.height || currentDimension.depth) / 1000).toFixed(2)} m`;
    }
    // Single value (radius/length)
    return `${(currentDimension / 1000).toFixed(2)} m`;
  }, [currentDimension]);

  return {
    // State
    phase,
    firstPoint,
    currentMousePos,
    preview,
    currentDimension,
    formattedDimension,
    currentAngle,
    dimensionInput,
    showDimensionInput,
    config,

    // Flags
    isDrawingTool,
    isActive: phase !== DRAWING_PHASES.IDLE,
    isWaitingForSecondPoint: phase === DRAWING_PHASES.SECOND_POINT,

    // UI
    statusText,

    // Actions
    startDrawing,
    updateMousePosition,
    confirmWithMouse,
    confirmWithDimension,
    getPointFromDimension,
    setDimensionInput,
    showInput,
    hideInput,
    reset,

    // Constants
    DRAWING_PHASES,
  };
}

export default useDrawingTools;
